import sentryConfig from 'src/store/sentry.js'

import { App } from '@capacitor/app'
import { Share } from '@capacitor/share'
import { Badge } from '@capawesome/capacitor-badge'
import { AppUpdate } from '@capawesome/capacitor-app-update'
import { AppLauncher } from '@capacitor/app-launcher'
import { Haptics, ImpactStyle } from '@capacitor/haptics'
import { Keyboard } from '@capacitor/keyboard'
import { Browser } from '@capacitor/browser'
import { CapacitorHttp, Capacitor, registerPlugin } from '@capacitor/core'
import { LiveUpdate } from '@capawesome/capacitor-live-update'

import clyTracking from 'src/config/services/countlyTracking/clyTracking.js'
import trackingUtils from 'src/config/services/countlyTracking/utils.js'
import isMobileDeviceMixin from 'src/mixins/miscellaneous/isMobileDeviceMixin.js'
import { ScreenReader } from '@capacitor/screen-reader'
import GlobalPrototypes from '@/config/services/globalPrototypes.ts'
import { Preferences } from '@capacitor/preferences'

const androidApi = typeof android === 'undefined' ? null : android
const webkitApi = typeof webkit === 'undefined' ? null : webkit
const isAndroid = () => (androidApi && androidApi.version) || Capacitor.getPlatform() === 'android'
const isIOS = () => (webkitApi?.messageHandlers && window.iosAppVersion) || Capacitor.getPlatform() === 'ios'

const AppUtilsPlugin = registerPlugin('AppUtilsPlugin')
const BrandingPlugin = registerPlugin('BrandingPlugin')
const ShortcutsPlugin = registerPlugin('ShortcutsPlugin')
const EnvironmentPlugin = registerPlugin('EnvironmentPlugin')
const PushNotifications = registerPlugin('PushNotificationsPlugin')
const AccessibilityPlugin = registerPlugin('AccessibilityPlugin')
const LinkUtilsPlugin = registerPlugin('LinkUtilsPlugin')

const CAPACITOR_VERSION = '4.0.0'
const buildSentryError = (message) => ({ category: 'error', level: 'warning', message })
const PUSH_CONSENT = { ACCEPTED: 'ACCEPTED', DENIED: 'DENIED', PENDING: 'PENDING' }

const native = {
  isIOS,
  isAndroid,

  async isCapacitorSupported () {
    return await this.isAboveVersion(CAPACITOR_VERSION)
  },

  async isScreenreaderPluginSupported () {
    return await this.isAboveVersion('4.0.6')
  },

  async isPreferencesPluginSupported () {
    return await this.isAboveVersion('4.0.9') || !this.isNative()
  },

  async isInAppBrowserIframeSupported () {
    if (!this.isNative()) return true
    const isIOS = this.isIOS()
    const isAndroid = this.isAndroid()

    if (isAndroid) return true

    return isIOS && await this.isAboveVersion('4.0.10')
  },

  compareVersions (currentVersion, targetVersion) {
    const current = currentVersion.split('.')
    const target = targetVersion.split('.')
    const len = Math.max(current.length, target.length)

    for (let i = 0; i < len; i++) {
      const num1 = parseInt(current[i] || '0', 10)
      const num2 = parseInt(target[i] || '0', 10)
      if (num1 !== num2) return num1 - num2
    }

    return 0
  },

  async isAboveVersion (targetVersion) {
    const version = await this.getVersion()
    if (!version) return false
    return this.compareVersions(version, targetVersion) >= 0
  },

  async isBelowVersion (targetVersion) {
    const version = await this.getVersion()
    if (!version) return false
    return this.compareVersions(version, targetVersion) < 0
  },

  async isOlderNativeVersion () {
    return await this.isBelowVersion(CAPACITOR_VERSION)
  },

  async isInitUserAuthenticated () {
    return !!GlobalPrototypes.store.state.authToken || !!await GlobalPrototypes.store.dispatch('loadAuthToken', null, { root: true })
  },

  isNative: () => isAndroid() || isIOS() || false,

  getDevice: () => {
    if (isAndroid()) return 'android'
    else if (isIOS()) return 'ios'
    return 'web'
  },

  /*
    Because of our capacitor + WKWebView setup, iOS is not able to respect the system font-size from the user.
    So we get the system font-size from the native side and set an appropriate font-size in the app.
    This is only needed for iOS, because Android is already respecting the system font-size.
    NOTE: We only increase font-size up to 2rem, because larger font-sizes break the layout of the app.
  */
  async getUserPreferredFontSettings () {
    // This is to catch capacitor app versions that doesnt have the Accessabillity plugin and are not iOS
    if (!isIOS() || !(await this.getVersion()) || await this.isBelowVersion('4.0.8')) {
      return {
        fontSize: null,
        boldTextEnabled: false,
      }
    }

    const preferredFontSettings = await AccessibilityPlugin.getPreferredFontSettings()

    /*
    * All possible enum values:
    * UICTContentSizeCategoryXS
    * UICTContentSizeCategoryS
    * UICTContentSizeCategoryM
    * UICTContentSizeCategoryL
    * UICTContentSizeCategoryXL
    * UICTContentSizeCategoryXXL
    * UICTContentSizeCategoryXXXL
    * UICTContentSizeCategoryAccessibilityM
    * UICTContentSizeCategoryAccessibilityL
    * UICTContentSizeCategoryAccessibilityXL
    * UICTContentSizeCategoryAccessibilityXXL
    * UICTContentSizeCategoryAccessibilityXXXL
    */
    let transformedfontSize = '1rem'

    switch (preferredFontSettings.fontSize) {
    case 'UICTContentSizeCategoryXS':
      transformedfontSize = '0.76rem'
      break
    case 'UICTContentSizeCategoryS':
      transformedfontSize = '0.88rem'
      break
    case 'UICTContentSizeCategoryM':
      transformedfontSize = '0.94rem'
      break
    case 'UICTContentSizeCategoryL':
      transformedfontSize = '1rem'
      break
    case 'UICTContentSizeCategoryXL':
      transformedfontSize = '1.12rem'
      break
    case 'UICTContentSizeCategoryXXL':
      transformedfontSize = '1.23rem'
      break
    case 'UICTContentSizeCategoryXXXL':
      transformedfontSize = '1.35rem'
      break
    case 'UICTContentSizeCategoryAccessibilityM':
      transformedfontSize = '1.65rem'
      break
    case 'UICTContentSizeCategoryAccessibilityL':
      transformedfontSize = '1.94rem'
      break
    case 'UICTContentSizeCategoryAccessibilityXL':
      transformedfontSize = '2rem' // 2.35rem
      break
    case 'UICTContentSizeCategoryAccessibilityXXL':
      transformedfontSize = '2rem'  // 2.76rem
      break
    case 'UICTContentSizeCategoryAccessibilityXXXL':
      transformedfontSize = '2rem'  // 3.11rem
      break
    }

    return {
      fontSize: transformedfontSize,
      boldTextEnabled: preferredFontSettings.boldTextEnabled || false,
    }
  },

  async initialize () {
    await this.initPushNotifications()
    await this.resetNativeBadge()
    await this.initNativeWrapper()
    await this.setupKeyboard()
    await this.initScreenReaderStateListener()
    await this.setupSafariClosedListener()
    await this.setupDeepLinkListener()
  },

  /**
   * Sets up a global deep link listener for OIDC authentication callbacks.
   * To allow for deep linking into the app from the browser.
   * Needs to be global to prevent the listener from being destroyed when the component is destroyed.
   */
  async setupDeepLinkListener () {
    if (!this.isNative() || !await this.isCapacitorSupported()) return

    App.addListener('appUrlOpen', (data) => {
      const url = new URL(data.url)
      const relativePath = url.pathname + url.search + url.hash
      GlobalPrototypes.router.push(relativePath)
    })
  },

  async setupSafariClosedListener () {
    if (!(await this.isCapacitorSupported()) || !isIOS() || !this.isNative()) return
    LinkUtilsPlugin.addListener('safariClosed', ({ widgetId }) => {
      trackingUtils.closeFeedbackSurvey({ widgetId })
      const surveyIframe = document.getElementById(`countly-surveys-wrapper-${widgetId}`)
      surveyIframe && surveyIframe.remove()
    })
  },

  async initNativeWrapper () {
    if (await this.isCapacitorSupported()) return

    if (isAndroid() && await this.isAboveVersion('3.4.3')) {
      return androidApi.Load()
    }

    if (isAndroid() && await this.isBelowVersion('3.4.3')) {
      return await this.loadPushToken()
    }

    this.triggerIOSLoad()
    this.loadPushToken()
  },

  async setNativeShortcuts (items = []) {
    if (!this.isNative() || await this.isOlderNativeVersion()) return

    const { supported } = await ShortcutsPlugin.isShortcutsSupported()
    if (!supported) return

    return ShortcutsPlugin.setShortcuts({ items })
  },

  async listenNativeShortcuts (cb) {
    if (!this.isNative() || await this.isOlderNativeVersion()) return

    return ShortcutsPlugin.addListener('onPressShortcut', (item) => {
      if (!item?.type) return
      return cb(item.type.split('.')[3])
    })
  },

  async notifyFrontendReady () {
    if (!this.isNative() || await this.isOlderNativeVersion()) return

    return AppUtilsPlugin.notifyFrontendReady()
  },

  initNativeStoreState (store) {
    if (!this.isNative()) return
    store.commit('SET_IS_NATIVE', true)
    store.commit('SET_IS_ANDROID', isAndroid())
    store.commit('SET_IS_IOS', isIOS())
  },

  async initPushNotifications () {
    if (!this.isNative()) return

    if (await this.isOlderNativeVersion()) {
      window.updateFCMTokeniOS = window.updateFCMTokenAndroid = (token, consent) => this.setPushToken(token, consent)
      return
    }

    await PushNotifications.addListener('registration', async (token) => {
      const { receive } = await PushNotifications.checkPermissions()
      return this.setPushToken(token.value, receive)
    })

    await PushNotifications.addListener('registrationError', err => {
      sentryConfig.addBreadcrumb(buildSentryError(`registrationError: ${err?.error}`))
      sentryConfig.throwError(`Error requesting push notification permission ${this.getDevice()}`)
    })

    await PushNotifications.addListener('pushNotificationReceived', () => {
      return clyTracking.trackNotification().action.receivePush()
    })

    // this might not be needed, its only use-case it when a user is not yet logged in
    await this.setPushNotificationConsent()

    // this needs to be called in case of iPhone migration:
    // the permissions are copied over but
    // push still need to be registered
    await this.registerPushNotification()
  },

  async listenPushNotificationsAction (cb, _window) {
    try {
      if (!this.isNative()) return

      if (await this.isOlderNativeVersion()) {
        const pushData = _window.iosLastPushData || _window.androidLastPushData || _window.iosLongPressData
        if (pushData) return cb(pushData)

        _window.setIOSPushData = _window.setAndroidPushData = _window.setIOSLongPressData = (pushData) => {
          clyTracking.trackNotification().action.openPush()
          return cb(pushData)
        }

        return
      }

      return await PushNotifications.addListener('pushNotificationActionPerformed', ({ notification }) => {
        clyTracking.trackNotification().action.openPush()
        return cb(notification.data)
      })
    } catch (e) {
      sentryConfig.addBreadcrumb(buildSentryError(`${e?.name}: ${e?.message}`))
      sentryConfig.throwError(`Error listening to push notifications ${this.getDevice()}`)
    }
  },

  async hasPushNotifications () {
    if (!this.isNative()) return

    if (await this.isOlderNativeVersion()) {
      return GlobalPrototypes.store?.state?.device?.consent === PUSH_CONSENT.ACCEPTED
    }

    const permission = await PushNotifications.checkPermissions()
    return permission.receive === 'granted'
  },

  async setPushNotificationConsent () {
    if (!this.isNative() || await this.isOlderNativeVersion()) return

    const { receive } = await PushNotifications.checkPermissions()
    const formattedConsent = await this.getFormattedPushConsent(receive)

    return this.trackPushConsent(formattedConsent)
  },

  async unregisterPushNotifications () {
    await PushNotifications.unregister()
  },

  async registerPushNotification () {
    try {
      if (!this.isNative()) return

      if (!await this.isInitUserAuthenticated()) return

      if (await this.isCapacitorSupported()) {
        let permission = await PushNotifications.checkPermissions()
        this.trackPushConsent(await this.getFormattedPushConsent(permission.receive))

        if (permission.receive === 'prompt') permission = await PushNotifications.requestPermissions()
        if (permission.receive !== 'granted') return

        const isInitializingFirebaseAfterConsentAndroid = isAndroid() && await this.isAboveVersion('4.1.4')
        const isInitializingFirebaseAfterConsentIos = isIOS() && await this.isAboveVersion('4.1.5')

        if (isInitializingFirebaseAfterConsentAndroid || isInitializingFirebaseAfterConsentIos) {
          await PushNotifications.initializeFirebase()
        }
        return await PushNotifications.register()
      }

      // Android: run time permission dialog was implemented with 3.4
      if (isAndroid() && await this.isAboveVersion('3.4')) return androidApi.requestPushNotificationPermission()
      if (isIOS()) return webkitApi.messageHandlers.AskPush.postMessage('')
    } catch (e) {
      sentryConfig.addBreadcrumb(buildSentryError(`${e?.name}: ${e?.message}`))
      sentryConfig.throwError(`Error requesting push notification permission in "setPushNotification" ${this.getDevice()}`)
      sentryConfig.throwError(e)
    }
  },

  async resetPushNotification () {
    if (!this.isNative() || await this.isOlderNativeVersion()) return

    const { registered = false } = await PushNotifications?.checkRegistered?.()
    if (!await this.hasPushNotifications() || !registered) return

    return PushNotifications.removeAllDeliveredNotifications()
  },

  async setNativeBadge (count) {
    if (await this.isOlderNativeVersion() && isIOS()) {
      return webkitApi.messageHandlers.SetBadge.postMessage(count)
    }

    if (await this.isCapacitorSupported()) {
      // We should not set the badge counter to any other number than 0 if we don't have push permissions.
      // OS should already handle it but let's be sure
      if (count > 0 && !await this.hasPushNotifications()) return
      return await Badge.set({ count })
    }
  },

  async resetNativeBadge () {
    if (!this.isNative()) return

    if (await this.isOlderNativeVersion() && isIOS()) {
      return webkitApi.messageHandlers.SetBadge.postMessage(0)
    }

    await this.setNativeBadge(0)
    await this.resetPushNotification()
  },

  triggerIOSLoad () {
    if (!isIOS()) return

    webkitApi.messageHandlers.Load.postMessage('')
  },

  async loadPushToken () {
    if (!this.isOlderNativeVersion) return

    if (isAndroid()) {
      // firebase can take a few seconds to set the token
      return setTimeout(async () => {
        const pushToken = await this.getAndroidToken()

        this.setPushToken(pushToken)
      }, 10 * 1000)
    }

    if (!isIOS()) return
    // WARNING: initNativeWrapper needs to be called before

    // Load() triggers async push token saving for newer versions
    if (await this.isAboveVersion('3.3.28')) return

    // older version use tokenChanged function to get save token in localStorage
    setTimeout(() => GlobalPrototypes.store.dispatch('savePushToken'), 10 * 1000)
  },

  /**
  * Sets the push token in the store.
  *
  * @param {string|null} token - The push token to be set.
  * @param {boolean|string|null} [pushNotificationConsent=null] - The user's consent for push notifications. On iOS it can be true, false or null. On Android its already a string 'ACCEPTED', 'DENIED' or 'PENDING'.
  * @returns {void} - Dispatches the action to save the push token in the store.
  */
  async setPushToken (token, pushNotificationConsent = null) {
    /*
    * Clean up localStorage if token is null.
    * Because in the past, the token could turn out to be null or an object, which got saved in the localStorage.
    * Remove this part, when sentry errors is going down and we are sure that this is not needed anymore
    */
    if (!token) {
      const savedToken = GlobalPrototypes.store.state.device.firebaseToken

      // if token is 'null' or an '[object Object]', remove it from the localStorage
      if (savedToken !== 'null' && savedToken !== '[object Object]') return
      return GlobalPrototypes.store.dispatch('device/removeFirebaseToken')
    }

    GlobalPrototypes.store.dispatch('device/saveFirebaseToken', token)

    const consentState = await this.getFormattedPushConsent(pushNotificationConsent)

    this.trackPushChanges(token)
    this.trackPushConsent(consentState)

    return GlobalPrototypes.store.dispatch('savePushToken', { token, consentState })
  },

  async getAndroidToken () {
    /*
    * For android verion 3.3.27 and newer and if "getFirebaseToken" is guven versions use firebase token
    * otherwise use the old token (for older versions)
    */
    if (androidApi.getFirebaseToken || await this.isAboveVersion('3.4.2')) {
      return androidApi.getFirebaseToken()
    }

    const data = androidApi.loadDATA()

    if (!data.includes('token')) return null

    // there can be multiple token in this string
    const parts = data.split("token = '")
    return parts[parts.length - 1].split('\'')[0]
  },

  /**
  * Function to track changes in pushData.
  *
  * @param {string} pushData - The new push data value.
  * @return {undefined} Returns nothing.
  */
  trackPushChanges (pushData) {
    const localStoragePushData = GlobalPrototypes.store.state.device.firebaseToken

    if (!localStoragePushData || !pushData) return

    if (localStoragePushData === pushData) return

    // if given pushData and localStoragePushData are not the same, fire tracking event
    return clyTracking.trackNotification().action.tokenChangePush()
  },

  async getFormattedPushConsent (pushNotificationConsent) {
    if ((isIOS() && await this.isBelowVersion('3.3.33')) || (isAndroid() && await this.isBelowVersion('3.4.3'))) return null

    // Android already returns a string with one of the states, we don't need to format it
    if (isAndroid() && await this.isOlderNativeVersion()) {
      return pushNotificationConsent
    }

    const states = {
      prompt: PUSH_CONSENT.PENDING,
      granted: PUSH_CONSENT.ACCEPTED,
      denied: PUSH_CONSENT.DENIED,
      'prompt-with-rationale': PUSH_CONSENT.PENDING,
      null: PUSH_CONSENT.PENDING,
      true: PUSH_CONSENT.ACCEPTED,
      false: PUSH_CONSENT.DENIED
    }

    return states[pushNotificationConsent]
  },

  async trackPushConsent (pushNotificationConsent) {
    const localStoragePushData = GlobalPrototypes.store.state.device.consent

    if (!pushNotificationConsent) return

    // prevent re-tracking if consent is the same
    if (localStoragePushData === JSON.stringify(pushNotificationConsent)) return

    await GlobalPrototypes.store.dispatch('device/saveNotificationConsent', pushNotificationConsent)
    // return tracking.addEvent('push-action', { action: pushNotificationConsent ? 'enable-consent' : 'disable-consent' })
  },

  async getVersion () {
    if (!this.isNative()) return null

    // Backward compatibility for no capacitor versions
    if (isAndroid() && androidApi?.version) return androidApi.version()
    if (isIOS() && window?.iosAppVersion) return window.iosAppVersion

    const { version } = await App.getInfo()
    return version
  },

  async getPackageName () {
    if (this.isNative() && await this.isCapacitorSupported()) {
      const { id } = await App.getInfo()
      return id
    }

    // backwards compatibility for no capacitor versions
    if (isAndroid() && await this.isAboveVersion('3.3.29')) {
      return androidApi.packageName()
    }

    if (isIOS() && await this.isAboveVersion('3.3.24')) {
      return window.iosPackageName || null
    }

    return null
  },

  async getBranding () {
    if ((isAndroid() || isIOS()) && await this.isCapacitorSupported()) {
      const { flavor } = await BrandingPlugin.getBranding()

      return flavor.toUpperCase()
    }

    if (isAndroid() && await this.isAboveVersion('3.3.29')) {
      return androidApi?.branding?.()
    }

    if (isIOS() && await this.isAboveVersion('3.3.24')) {
      return window.iosBranding || null
    }

    return null
  },

  async share ({ title, url }) {
    const canShare = await this.canShare()

    if (await this.isCapacitorSupported() && canShare) return await Share.share({ title, url })

    // backwards compatibility for no capacitor versions
    if (isAndroid() && canShare) return androidApi.share(JSON.stringify({ text: url }))

    try {
      await navigator?.share?.({ title, url })
    } catch {}
  },

  reload () {
    location.reload()
  },

  async canShare () {
    if (await this.isCapacitorSupported()) {
      const { value } = await Share.canShare()
      return value
    }

    // backwards compatibility for no capacitor versions
    return navigator?.share || (isAndroid() && androidApi.share)
  },

  async supportVoiceRecording () {
    if (isAndroid()) return await this.isAboveVersion('3.3.27')
    return true
  },

  async minimizeView (val = true) {
    if (await this.isCapacitorSupported()) return
    if (!isIOS() || isMobileDeviceMixin.computed.isIpadOS()) return
    if (await this.isBelowVersion('3.3.22')) return
    webkitApi.messageHandlers.minimizeViewOnInput.postMessage(val)
  },

  async isLiveUpdateSupported () {
    if (import.meta.env.VITE_LIVE_UPDATE_ENABLED !== 'true') return false

    // All needed native logic was deployed with 4.2.0 (same for both Android + iOS)
    return await this.isAboveVersion('4.2.0')
  },

  async switchToLiveUpdateEnvironment (url) {
    try {
      const response = await CapacitorHttp.get({
        url: `${url}/bundles/latest.json`,
        headers: { 'Cache-Control': 'no-store' },
        readTimeout: 10000,
        connectTimeout: 10000,
      })

      if (response.status < 200 || response.status >= 300) {
        return GlobalPrototypes.toastNotify({ type: 'error', text: `Could not reach environment: ${url}` })
      }

      const { bundleId, url: rawUrl } = response.data
      const bundleUrl = rawUrl.startsWith('http') ? rawUrl : `${url}${rawUrl}`
      await LiveUpdate.downloadBundle({ bundleId, url: bundleUrl, artifactType: 'manifest' })
      await LiveUpdate.setNextBundle({ bundleId })

      // Before switching to another env, we reset all locally stored data to ensure the user is not logged in anymore
      await GlobalPrototypes.store.dispatch('users/resetCacheStorage')
      await GlobalPrototypes.store.dispatch('users/resetLocalStorage')
      await GlobalPrototypes.store.dispatch('users/resetOidcStorage')

      await LiveUpdate.reload()
    } catch (e) {
      console.error(e)
      await GlobalPrototypes.toastNotify({
        type: 'error',
        duration: 5000,
        text: 'Could not switch to target env. Check console!'
      })
    }
  },

  async switchEnvironment (environmentKey) {
    const environment = this.environments[environmentKey]
    if (!environment) {
      console.error(`environment not found (${environmentKey})`)
      return
    }

    if (!this.isNative()) {
      location.href = environment.url
      return
    }

    if (await this.isCapacitorSupported()) {
      if (await this.isLiveUpdateSupported()) return await this.switchToLiveUpdateEnvironment(environment.url)
      return await EnvironmentPlugin.switchEnv({ env: environmentKey })
    }

    // backwards compatibility for no capacitor versions
    if (isAndroid()) return androidApi.saveURL(environmentKey)
    if (isIOS()) return webkitApi.messageHandlers.changeURL.postMessage(environmentKey)
  },

  async switchToSduxEnvironment (url) {
    if (!this.isNative()) {
      location.href = url
      return
    }

    if (await this.isOlderNativeVersion()) {
      return GlobalPrototypes.toastNotify({
        type: 'error',
        text: 'This feature is not supported in non-capacitor (pre 4.0.0) app version)'
      })
    }

    if (await this.isLiveUpdateSupported()) return await this.switchToLiveUpdateEnvironment(url)
    return await EnvironmentPlugin.switchEnv({ env: url })
  },

  async canOpenAppUrl (url) {
    if (!await this.isCapacitorSupported()) return false

    const { value } = await AppLauncher.canOpenUrl({ url })
    return value
  },

  /*
  * Open a specific app (e.g. sdui-meet, by prodiving the package name) or a normal URL.
  *
  * When providing a normal URL. This function will open the URL in the system browser.
  * Sometimes we need this function for normal URL's.
  * Because Capacitor will open all URL's, that is from the same host or are defined in "allowNavigation" (in capacitor.config.ts), INSIDE the app.
  * But there are some cases where we want to open the URL in the system browser. In those cases, use this function.
  */
  async openAppUrl (url) {
    if (!await this.isCapacitorSupported()) return window.open(url, '_blank')

    return await AppLauncher.openUrl({ url })
  },

  async openUrlInAppBrowser (url) {
    // this is to catch capacitor app versions that doesnt have the Browser plugin
    if (await this.isBelowVersion('4.0.3')) {
      // Add this timeout to fix open new tab in ios safari browser and ios native app
      setTimeout(() => {
        const link = document.createElement('A')
        link.href = url
        link.target = '_blank'
        document.body.appendChild(link)
        link.setAttribute('hidden', '')

        link.click()

        // remove link
        setTimeout(() => {
          document.body.removeChild(link)
        }, 1000)
      }, 0)
      return
    }

    return await Browser.open({ url })
  },

  async openAppStore (url) {
    // this is to catch capacitor app versions that doesnt have the AppUpdate plugin
    if (await this.isBelowVersion('4.0.2')) return window.open(url, '_blank')

    return AppUpdate.openAppStore()
  },

  environments: {
    PRODUCTION: {
      url: 'https://sdui.app',
    },
    PRODUCTION_CH: {
      url: 'https://connect.pupil.ch',
    },
    PRE_PRODUCTION: {
      url: 'https://sduy.de',
    },
    MAIN_DEV: {
      url: 'https://mono-main.sdux.de',
    }
  },

  async vibrate () {
    if (isAndroid()) return // we don't want to vibrate on Android

    if (await this.isCapacitorSupported()) return await Haptics.vibrate()

    // backwards compatibility for no capacitor versions
    try {
      const vibrateType = 1519
      const smallTimeout = 100 // ms
      const arr = [[vibrateType, smallTimeout]]
      webkitApi.messageHandlers.VibratePattern.postMessage(arr)
    } catch {}
  },

  async hapticsImpact () {
    if (isAndroid()) return // we don't want to vibrate on Android

    if (await this.isCapacitorSupported()) return await Haptics.impact({ style: ImpactStyle.Medium })

    // backwards compatibility for no capacitor versions
    try {
      const vibrateType = 1519
      const smallTimeout = 100 // ms
      const arr = [[vibrateType, smallTimeout]]
      webkitApi.messageHandlers.VibratePattern.postMessage(arr)
    } catch {}
  },

  async setupKeyboard () {
    if (!(await this.isCapacitorSupported())) return

    Keyboard.addListener('keyboardWillShow', async (e) => {
      GlobalPrototypes.store.commit('SET_SOFT_KEYBOARD_HEIGHT', e.keyboardHeight)
    })
    Keyboard.addListener('keyboardWillHide', async () => {
      GlobalPrototypes.store.commit('SET_SOFT_KEYBOARD_HEIGHT', 0)
    })

    if (!isIOS()) return

    // iOS: show accessory bar by default
    this.setKeyboardAccessoryBar()

    // make sure page is scrolled to focused element when keyboard is shown (Bug only appears on iOS)
    Keyboard.addListener('keyboardDidShow', () => {
      const focusedElement = document.activeElement
      if (!focusedElement) return
      setTimeout(() => focusedElement.scrollIntoView({ behavior: 'smooth', block: 'center' }), 0)
    })
  },

  // show accessory bar in keyboard (only supported in iOS)
  async setKeyboardAccessoryBar (isVisible = true) {
    if (!isIOS() || !(await this.isCapacitorSupported())) return
    Keyboard.setAccessoryBarVisible({ isVisible })
  },

  async initScreenReaderStateListener () {
    if (!this.isNative() || !await this.isCapacitorSupported() || !await this.isScreenreaderPluginSupported()) return
    await ScreenReader.addListener('stateChange', ({ value }) => {
      GlobalPrototypes.store.commit('SET_IS_SCREEN_READER_ACTIVE', !!value)
    })

    // stateChange listener only fires when SR state was changed, but not when app was started while SR was already activated
    const screenReaderActiveOnAppStart = await ScreenReader.isEnabled()
    GlobalPrototypes.store.commit('SET_IS_SCREEN_READER_ACTIVE', screenReaderActiveOnAppStart?.value || false)
  },

  async getFromNativePreferences (key) {
    if (!await this.isPreferencesPluginSupported()) {
      // if plugin is not supported due to old native version, we fallback to previous behaviour which is localStorage
      return JSON.parse(localStorage?.getItem(key) || '[]')
    }

    const pref = await Preferences.get({ key })
    if (!pref || !pref.value) return
    const parsedPref = JSON.parse(pref.value)
    return parsedPref.length ? parsedPref : undefined
  },

  async setNativePreferences (key, value) {
    if (!await this.isPreferencesPluginSupported()) {
      // if plugin is not supported due to old native version, we fallback to previous behaviour which is localStorage
      return localStorage?.setItem(key, value)
    }
    await Preferences.set({
      key: key,
      value: value
    })
  },

  async removeNativePreferences (key) {
    if (!await this.isPreferencesPluginSupported()) {
      // if plugin is not supported due to old native version, we fallback to previous behaviour which is localStorage
      return localStorage?.removeItem(key)
    }
    await Preferences.remove({ key: key })
  }
}

export default native
